import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { game, PLAYER, RIVAL, roomCenter, type Pose } from '../game/controller';
import { useGame } from '../game/store';
import { audio } from '../audio/engine';
import { buildRooms, panelTexture, shutterTexture, ROOM_LAYERS, type RoomLayer } from './rooms';
import { Armor } from './Armor';
import { Caches, ScorchMark, Seals } from './Props';
import { Ghost } from './Ghost';
import { LAYER_MAIN_ONLY, Mirrors } from './Mirror';

const FOG = 0x0e1013;

/** The first-person world: the rooms around the player, the core and the rival. */
export function Scene() {
  const { camera, gl, scene } = useThree();
  const sceneVersion = useGame((s) => s.sceneVersion);
  const rivalArmor = useGame((s) => s.rivalArmor);
  const lamp = useRef<THREE.PointLight>(null);
  const coreLight = useRef<THREE.PointLight>(null);
  const core = useRef<THREE.Group>(null);
  const gem = useRef<THREE.Mesh>(null);
  const rival = useRef<THREE.Group>(null);
  const rivalLight = useRef<THREE.PointLight>(null);
  const pose = useMemo<Pose>(() => ({ position: new THREE.Vector3(), quaternion: new THREE.Quaternion() }), []);
  const rivalPose = useMemo<Pose>(() => ({ position: new THREE.Vector3(), quaternion: new THREE.Quaternion() }), []);

  useEffect(() => {
    scene.background = new THREE.Color(FOG);
    scene.fog = new THREE.FogExp2(FOG, 0.072);
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1.15;
  }, [gl, scene]);

  const materials = useMemo(() => {
    const tex = panelTexture(gl.capabilities.getMaxAnisotropy());
    const m: Record<RoomLayer, THREE.Material> = {
      wall: new THREE.MeshStandardMaterial({ map: tex, roughness: 0.6, metalness: 0.35, side: THREE.DoubleSide, vertexColors: true }),
      jamb: new THREE.MeshStandardMaterial({ color: 0x6f757e, roughness: 0.45, metalness: 0.55, side: THREE.DoubleSide }),
      trail: new THREE.MeshBasicMaterial({ color: 0xffb000, side: THREE.DoubleSide }),
      idle: new THREE.MeshBasicMaterial({ color: 0x4f555e, side: THREE.DoubleSide }),
      bars: new THREE.MeshBasicMaterial({ color: 0xcfc8ba, side: THREE.DoubleSide }),
      oneway: new THREE.MeshBasicMaterial({ map: shutterTexture(), color: 0xffffff, transparent: true, opacity: 0.85, side: THREE.FrontSide, depthWrite: false }),
    };
    return m;
  }, [gl]);

  const geometries = useMemo(() => {
    const m = game.match;
    if (!m) return null;
    return buildRooms(m.maze, game.visible, game.trail);
    // sceneVersion changes whenever the visible rooms or the trail change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneVersion]);

  useEffect(
    () => () => {
      if (geometries) Object.values(geometries).forEach((g) => g.dispose());
    },
    [geometries],
  );

  useFrame(() => {
    const m = game.match;
    if (!m) return;
    game.tick();
    game.pose(PLAYER, pose);
    camera.position.copy(pose.position);
    camera.quaternion.copy(pose.quaternion);
    lamp.current?.position.copy(pose.position);

    // Online, the core's room is known only once the server says it's in view.
    const coreRoom = game.mode === 'online' ? m.viewFor(PLAYER).coreRoom : m.maze.core;
    const coreVisible = coreRoom !== null && coreRoom >= 0 && game.visible.has(coreRoom);
    if (core.current) {
      core.current.visible = coreVisible;
      if (coreVisible) roomCenter(m.maze, coreRoom, core.current.position);
    }
    if (coreLight.current) {
      coreLight.current.intensity = coreVisible ? 25 : 0;
      if (coreVisible) roomCenter(m.maze, coreRoom, coreLight.current.position);
    }
    if (gem.current) {
      gem.current.rotation.y += 0.02;
      gem.current.rotation.x += 0.011;
    }

    // The rival is drawn only when this player's view says it's visible through doorways.
    const view = m.viewFor(PLAYER);
    const visible = view.rival !== null;
    if (visible !== useGame.getState().rivalVisible) useGame.setState({ rivalVisible: visible });
    if (rival.current) {
      rival.current.visible = visible;
      if (visible) {
        game.pose(RIVAL, rivalPose);
        rival.current.position.copy(rivalPose.position);
        rival.current.quaternion.copy(rivalPose.quaternion);
      }
    }
    if (rivalLight.current) {
      rivalLight.current.intensity = visible ? 12 : 0;
      rivalLight.current.position.copy(rivalPose.position);
    }

    const s = useGame.getState();
    if (s.phase === 'playing') {
      const t = s.threatBand, c = s.coreBand;
      const tension = t === 'danger' || c === 'hot' ? 0.55 : t === 'near' || c === 'warm' ? 0.7 : t === 'detected' || c === 'faint' ? 0.85 : 1;
      audio.dreadTick(performance.now(), tension);
    }
  });

  return (
    <>
      <ambientLight color={0x9aa3b0} intensity={0.55} />
      <pointLight ref={lamp} color={0xffe2b0} intensity={20} distance={13} decay={2} />
      <pointLight ref={coreLight} color={0x3fd0d4} intensity={0} distance={7} decay={2} />
      <pointLight ref={rivalLight} color={0xa9c4db} intensity={0} distance={4.5} decay={2} />
      {geometries &&
        ROOM_LAYERS.map((layer) => <mesh key={`${layer}-${sceneVersion}`} geometry={geometries[layer]} material={materials[layer]} />)}
      <group ref={core}>
        <mesh ref={gem}>
          <octahedronGeometry args={[0.42, 0]} />
          <meshStandardMaterial color={0x3fd0d4} emissive={0x3fd0d4} emissiveIntensity={1.4} roughness={0.2} metalness={0.1} />
        </mesh>
        <mesh>
          <icosahedronGeometry args={[0.75, 0]} />
          <meshBasicMaterial color={0x3fd0d4} wireframe transparent opacity={0.45} />
        </mesh>
      </group>
      <group ref={rival}>
        <Armor armor={rivalArmor} side={0xa9c4db} layer={LAYER_MAIN_ONLY} />
      </group>
      <Seals />
      <Caches />
      <ScorchMark />
      <Ghost />
      <Mirrors />
    </>
  );
}
